// src/pages/Reserve/ReservationList.tsx
import React, { useEffect, useState } from 'react';
import {
  CalendarDays,
  Clock3,
  MapPin,
  Scissors,
  UserRound,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import CancelConfirmModal from './CancelConfirmModal';
import RescheduleReservationModal from './RescheduleReservationModal';
import SubpageHeader from './SubpageHeader';
import api from '../../lib/api';

type Reservation = {
  id: number;
  store_id: number;
  staff_id: number | null;
  store_name: string;
  staff_name: string | null;
  first_service_id?: number | null;
  service_name: string;
  category_name?: string | null;
  service_count?: number;
  duration_minutes: number;
  reservation_date: string;
  start_time: string;
  end_time: string;
  status: string;
};

const ReservationList: React.FC = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [targetId, setTargetId] = useState<number | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<Reservation | null>(null);

  const fetchReservations = async () => {
    try {
      const res = await api.get('/api/customer-app/reservation/list');
      setReservations(res.data);
    } catch (err) {
      toast.error('Failed to get reservation information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const openConfirmModal = (id: number) => {
    setTargetId(id);
    setShowModal(true);
  };

  const handleConfirmAction = async () => {
    if (targetId === null) return;

    try {
      await api.delete(`/api/customer-app/reservation/cancel/${targetId}`);
      setReservations((prev) =>
        prev.map((reservation) =>
          reservation.id === targetId
            ? { ...reservation, status: 'canceled' }
            : reservation
        )
      );
      toast.success('Reservation cancelled');
      setShowModal(false);
      setTargetId(null);
      fetchReservations();
    } catch (err) {
      toast.error('Cancellation failed');
    }
  };

  const now = new Date();
  const canRescheduleReservation = (reservation: Reservation): boolean => {
    if (!reservation.staff_id || reservation.duration_minutes <= 0) return false;

    const startAt = new Date(`${reservation.reservation_date}T${reservation.start_time}:00`);
    if (Number.isNaN(startAt.getTime())) return false;

    return startAt.getTime() - now.getTime() >= 24 * 60 * 60 * 1000;
  };

  const upcomingReservations = reservations
    .filter((rsv) => rsv.status === 'confirmed')
    .filter((rsv) => {
      const dt = new Date(`${rsv.reservation_date}T${rsv.end_time}:00`);
      return !Number.isNaN(dt.getTime()) && dt >= now;
    })
    .sort((a, b) => {
      const da = new Date(`${a.reservation_date}T${a.start_time}:00`).getTime();
      const db = new Date(`${b.reservation_date}T${b.start_time}:00`).getTime();
      return da - db;
    });

  const formatServiceLabel = (reservation: Reservation) => {
    const baseLabel = reservation.category_name && reservation.service_name
      ? `${reservation.category_name} – ${reservation.service_name}`
      : reservation.service_name || '-';

    return reservation.service_count && reservation.service_count > 1
      ? `${baseLabel} (+${reservation.service_count - 1} more)`
      : baseLabel;
  };

  const formatDisplayDate = (date: string) =>
    new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
    });

  const formatStatusLabel = (status: string) => {
    if (status === 'confirmed') return 'Confirmed';
    if (status === 'canceled') return 'Canceled';
    return status;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <SubpageHeader title="Booking" onBack={() => navigate('/reserve')} />
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500 shadow-sm">
          Loading...
        </div>
      </div>
    );
  }

  if (upcomingReservations.length === 0) {
    return (
      <div className="space-y-4">
        <SubpageHeader title="Booking" onBack={() => navigate('/reserve')} />
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-8 text-center shadow-sm">
          <div className="text-base font-semibold text-slate-900">No upcoming booking</div>
          <div className="mt-2 text-sm text-slate-500">
            Book your next visit when you are ready.
          </div>
          <button
            type="button"
            onClick={() => navigate('/reserve/booking')}
            className="mt-5 inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Book now
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <SubpageHeader title="Booking" onBack={() => navigate('/reserve')} />

        {upcomingReservations.map((rsv, index) => (
          <article
            key={rsv.id}
            className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_18px_44px_rgba(15,23,42,0.08)]"
          >
            <div className="bg-[linear-gradient(135deg,#0f172a,#1e293b)] px-5 py-4 text-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/65">
                    {index === 0 ? 'Next Booking' : 'Upcoming'}
                  </div>
                  <div className="mt-2 text-2xl font-semibold tracking-[-0.03em]">
                    {formatDisplayDate(rsv.reservation_date)}
                  </div>
                  <div className="mt-1 text-sm text-white/72">
                    {rsv.start_time} - {rsv.end_time}
                  </div>
                </div>
                <div className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/85">
                  {formatStatusLabel(rsv.status)}
                </div>
              </div>
            </div>

            <div className="space-y-4 px-5 py-5">
              <div className="text-lg font-semibold leading-snug tracking-[-0.02em] text-slate-950">
                {formatServiceLabel(rsv)}
              </div>

              <div className="grid gap-3 rounded-[24px] bg-slate-50 px-4 py-4">
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <span>{rsv.store_name}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <UserRound className="h-4 w-4 text-slate-400" />
                  <span>{rsv.staff_name || 'Anyone'}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <Scissors className="h-4 w-4 text-slate-400" />
                  <span>{formatServiceLabel(rsv)}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <CalendarDays className="h-4 w-4 text-slate-400" />
                  <span>{formatDisplayDate(rsv.reservation_date)}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <Clock3 className="h-4 w-4 text-slate-400" />
                  <span>{rsv.start_time} - {rsv.end_time}</span>
                </div>
              </div>

              <div className={`grid gap-3 ${canRescheduleReservation(rsv) ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {canRescheduleReservation(rsv) ? (
                  <button
                    type="button"
                    onClick={() => setRescheduleTarget(rsv)}
                    className="rounded-[18px] bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Change
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => openConfirmModal(rsv.id)}
                  className="rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      <CancelConfirmModal
        open={showModal}
        onCancel={() => setShowModal(false)}
        onConfirm={handleConfirmAction}
        message="Do you want to cancel this reservation?"
        confirmText="Cancel"
      />

      <RescheduleReservationModal
        open={Boolean(rescheduleTarget)}
        reservation={rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        onSuccess={fetchReservations}
      />
    </>
  );
};

export default ReservationList;
